import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowRight, ClipboardList, Wrench, Package, Sparkles, Truck, Users, Search, ShoppingCart, ListChecks } from 'lucide-react';

const Landing = () => {
  const { login } = useAuth();

  const trades = [
    'Carpenters', 'Electricians', 'Plumbers', 'Roofers',
    'Mechanics', 'Boat Repairers', 'HVAC Techs', 'Builders'
  ];

  const features = [
    { 
      icon: ShoppingCart, 
      title: 'Parts Shopping Made Easy', 
      desc: 'Every part in your list gets a one-click search link to find the best price online. No more switching between tabs — go straight from your list to the store.' 
    },
    { 
      icon: Package, 
      title: 'Smart Parts Lists', 
      desc: 'Build detailed parts lists per task with quantities and suppliers. Generate consolidated orders grouped by supplier, ready to print or share.' 
    },
    { 
      icon: Sparkles, 
      title: 'AI Suggests What You Need', 
      desc: 'Describe the job and AI will suggest the parts, tools, and steps you\'ll need. Add items to your list with a single click.' 
    },
    { 
      icon: ClipboardList, 
      title: 'Job & Task Tracking', 
      desc: 'Break down every job into tasks with status, time estimates, and due dates. Always know what\'s next on the list.' 
    },
    { 
      icon: Users, 
      title: 'Crew Management', 
      desc: 'Assign tasks to crew members, manage teams, and keep everyone on the same page across job sites.' 
    },
    { 
      icon: Wrench, 
      title: 'Tool Tracking', 
      desc: 'Keep a record of tools needed per task. Never show up to a job site missing the right gear.' 
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&w=1920&q=80)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/40" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <Wrench className="h-5 w-5 text-slate-950" />
              </div>
              <span className="text-white font-heading font-bold text-xl tracking-tight">TaskMaster</span>
            </div>
            <Button 
              onClick={login}
              variant="outline"
              className="border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white"
              data-testid="nav-login-btn"
            >
              Sign In
            </Button>
          </nav>

          <div className="py-24 sm:py-32 lg:py-40 max-w-2xl">
            <div className="flex flex-wrap gap-2 mb-8">
              {trades.map((trade) => (
                <span 
                  key={trade}
                  className="text-xs font-medium px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20"
                >
                  {trade}
                </span>
              ))}
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight font-heading leading-[1.1]">
              Plan the job. <span className="text-amber-400">Source the parts.</span> Get it done.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl">
              TaskMaster helps tradespeople plan every job, build parts lists with AI, 
              and find the best prices online &mdash; all before you leave the workshop. 
              No more wasted trips to the supplier.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={login}
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-8 h-12"
                data-testid="hero-get-started-btn"
              >
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Parts Shopping Highlight */}
      <div className="py-20 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-amber-400 font-semibold text-sm tracking-widest uppercase mb-3">Shop smarter</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-white font-heading">
                From parts list to shopping cart in seconds
              </h2>
              <p className="mt-4 text-base text-slate-400 leading-relaxed">
                Every part you add to a task gets a direct search link. When your list is ready,
                open the parts order view and click through to find the best price from any supplier.
                No copy-pasting part names. No switching between apps.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  { icon: ListChecks, text: 'Build your parts list as you plan each task' },
                  { icon: Sparkles, text: 'Let AI suggest parts and quantities you might need' },
                  { icon: Truck, text: 'Orders auto-grouped by supplier, ready to print or email' },
                  { icon: Search, text: 'One-click Google search to find the best price on every part' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-slate-300">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="h-4 w-4 text-amber-400" />
                    </div>
                    <span className="text-sm leading-relaxed">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
                <Package className="h-4 w-4" />
                <span className="font-medium text-slate-300">Parts Order Preview</span>
              </div>
              {[
                { part: '10mm Marine Plywood', qty: 3, supplier: 'Bunnings' },
                { part: 'Sikaflex 291 Sealant', qty: 2, supplier: 'Whitworths' },
                { part: 'M8 Stainless Bolts (pack 20)', qty: 1, supplier: 'Bolt Barn' },
                { part: '5200 Polyurethane Adhesive', qty: 1, supplier: 'Marine Deals' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium">{item.part}</p>
                    <p className="text-xs text-slate-500">{item.supplier}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">x{item.qty}</span>
                    <span className="text-xs text-amber-400 hover:text-amber-300 cursor-pointer underline">Search</span>
                  </div>
                </div>
              ))}
              <p className="text-xs text-slate-500 text-center pt-2">
                Click "Search" on any part to find the best price online
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="py-20 bg-slate-950 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-amber-400 font-semibold text-sm tracking-widest uppercase mb-3">How it works</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-heading">
              Three steps from quote to workshop
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { step: '01', title: 'Plan the job', desc: 'Create tasks, describe the work, set estimates. AI helps you figure out what parts and tools you\'ll need.' },
              { step: '02', title: 'Build your shopping list', desc: 'Add parts manually or accept AI suggestions. Track quantities, suppliers, and availability all in one place.' },
              { step: '03', title: 'Shop, order & go', desc: 'Click through to find the best price on every part. Print your supplier-grouped list or share it by email. Then get to work.' },
            ].map((item) => (
              <div key={item.step} className="relative">
                <span className="text-6xl font-bold text-slate-800 font-heading">{item.step}</span>
                <h3 className="text-lg font-semibold text-white font-heading mt-2">{item.title}</h3>
                <p className="mt-2 text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="py-20 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-amber-400 font-semibold text-sm tracking-widest uppercase mb-3">Features</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-heading">
              Built for the people who build things
            </h2>
            <p className="mt-4 text-base text-slate-400 max-w-2xl mx-auto">
              Every feature designed for tradespeople who need to source materials,
              coordinate crews, and get the job done without wasted trips.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className="bg-slate-950/50 rounded-xl p-6 border border-slate-800 hover:border-slate-700 transition-colors"
              >
                <div className="w-11 h-11 bg-amber-500/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="h-5 w-5 text-amber-400" />
                </div>
                <h3 className="text-base font-semibold text-white font-heading">{feature.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonial-style value prop */}
      <div className="py-20 bg-slate-950 border-t border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl sm:text-3xl font-bold text-white font-heading leading-snug">
            "I used to spend half my morning figuring out what parts I needed
            and driving to three different suppliers. Now I plan it all the night before
            and pick everything up in one trip."
          </p>
          <p className="mt-6 text-slate-500 text-sm">
            The kind of time savings TaskMaster delivers for tradespeople every day.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="py-20 bg-slate-900 border-t border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-heading">
            Stop guessing. Start sourcing smarter.
          </h2>
          <p className="mt-4 text-base text-slate-400">
            Plan your jobs, build your parts lists, and shop for materials &mdash; all from one place.
            Free to get started.
          </p>
          <Button 
            onClick={login}
            size="lg"
            className="mt-8 bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-8 h-12"
            data-testid="cta-get-started-btn"
          >
            Get Started Free
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-slate-600 text-sm">
          <p>&copy; 2026 TaskMaster. Built for the trades.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
